// eslint-disable typescript/no-unsafe-declaration-merging

import {
  FetchArguments,
  HttpClient,
  HttpClientOptions,
  HttpError,
  RequestLog,
  RequestOptions,
} from '../utils/http';

class ApiHttpClient extends HttpClient {
  private token?: Token;
  private refreshTokenCallback?: RefreshTokenCallback;

  constructor({ token, refreshTokenCallback, ...base }: ApiClientOptions = {}) {
    super(base);
    this.setToken(token);
    this.refreshTokenCallback = refreshTokenCallback;
  }

  setToken(tokens?: Token) {
    this.token = tokens;
  }

  setRefreshTokenCallback(refreshTokeFunction: RefreshTokenCallback) {
    this.refreshTokenCallback = refreshTokeFunction;
  }

  protected prepareFetchArguments(
    input: RequestInfo,
    init?: RequestOptions,
  ): FetchArguments {
    const args = super.prepareFetchArguments(input, init);

    const headers = args.init.headers as any;

    const token = this.token?.idToken;
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return args;
  }

  protected async handleResponseError<T>(
    response: Response,
    data: any,
    input: RequestInfo,
    init?: RequestOptions,
  ): Promise<HttpError | T> {
    const result = await super.handleResponseError(response, data, input, init);

    if (!(result instanceof Error)) {
      return result as T;
    }

    const error = result as HttpError;

    if (error.serverStack) {
      Object.assign(error, { stack: error.serverStack });
    }

    if (init?.disableSubRequest) {
      return error;
    }

    const code = error.error;
    if (code === 'auth/id-token-expired' || code === 'auth/argument-error') {
      await this.refreshIdToken();
      return this.request(input, { ...init, disableSubRequest: true });
    }

    return error;
  }

  private async refreshIdToken() {
    if (typeof this.refreshTokenCallback !== 'function') {
      throw new TypeError(`HttpClient.refreshTokenCallback is not set`);
    }

    if (!this.token) {
      throw new TypeError(`HttpClient.token is not set`);
    }

    this.token = await this.refreshTokenCallback(this.token.refreshToken);

    this.emit('token-refreshed', this.token);
  }
}

export default ApiHttpClient;

declare interface ApiHttpClient {
  on(event: 'log', listener: (log: RequestLog) => void): this;
  on(event: 'token-refreshed', listener: (token: Token) => void): this;
}

export interface ApiClientOptions extends HttpClientOptions {
  refreshTokenCallback?: RefreshTokenCallback;
  token?: Token;
}

export interface Token {
  idToken: string;
  refreshToken: string;
}

type RefreshTokenCallback = (refreshToken: string) => Promise<Token>;
